import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { BoundingBox } from "@/types";
import { resolveSurround, type SurroundStyle } from "@/components/listings/surroundStyle";

export type { SurroundStyle };

interface ZoomedListingImageProps {
  imageUrl: string;
  boundingBox: BoundingBox;
  alt: string;
  className?: string;
  aspectRatio?: number;
  paddingPx?: number;
  /**
   * Treatment applied to the area outside the bounding box. Defaults to "dim".
   */
  surroundStyle?: SurroundStyle;
  /**
   * @deprecated Use surroundStyle instead. Overrides darken when provided.
   */
  dimOpacity?: number;
  overlayContent?: (ctx: { Wi: number; Hi: number; crop: { x: number; y: number; w: number; h: number } }) => ReactNode;
}

interface NaturalSize {
  w: number;
  h: number;
}

export function ZoomedListingImage({
  imageUrl,
  boundingBox,
  alt,
  className,
  aspectRatio,
  paddingPx = 20,
  surroundStyle = "white-wash-light-grayscale-blur",
  dimOpacity,
  overlayContent,
}: ZoomedListingImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [natural, setNatural] = useState<NaturalSize | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const uid = useId().replace(/:/g, "");

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    setNatural(null);
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setNatural({ w: img.naturalWidth, h: img.naturalHeight });
      }
    };
    img.src = imageUrl;
  }, [imageUrl]);

  const containerStyle: React.CSSProperties = natural
    ? { aspectRatio: aspectRatio ?? `${natural.w} / ${natural.h}` }
    : aspectRatio
      ? { aspectRatio }
      : {};

  if (!natural || containerWidth === 0) {
    return (
      <div ref={containerRef} className={className} style={containerStyle}>
        <img
          src={imageUrl}
          alt={alt}
          className={aspectRatio ? "h-full w-full object-cover" : "w-full"}
        />
      </div>
    );
  }

  const { w: Wi, h: Hi } = natural;
  const imageAspect = Wi / Hi;
  const R = aspectRatio ?? imageAspect;
  const containerHeight = containerWidth / R;

  const bx = (boundingBox.xMin / 1000) * Wi;
  const by = (boundingBox.yMin / 1000) * Hi;
  const bw = ((boundingBox.xMax - boundingBox.xMin) / 1000) * Wi;
  const bh = ((boundingBox.yMax - boundingBox.yMin) / 1000) * Hi;

  const marginW = Math.min(0.35, paddingPx / containerWidth);
  const marginH = Math.min(0.35, paddingPx / containerHeight);
  const bwPadded = bw / Math.max(0.001, 1 - 2 * marginW);
  const bhPadded = bh / Math.max(0.001, 1 - 2 * marginH);

  const maxCw = Math.min(Wi, Hi * R);
  let cw = Math.max(bwPadded, bhPadded * R, containerWidth);
  cw = Math.min(cw, maxCw);
  const ch = cw / R;

  const bboxCenterX = bx + bw / 2;
  const bboxCenterY = by + bh / 2;
  let cropX = bboxCenterX - cw / 2;
  let cropY = bboxCenterY - ch / 2;
  cropX = Math.max(0, Math.min(Wi - cw, cropX));
  cropY = Math.max(0, Math.min(Hi - ch, cropY));

  const surround = resolveSurround(surroundStyle);
  const effectiveDarken = dimOpacity ?? surround.darken;

  // Convert display blur to image-unit blur (viewBox scale = cw / containerWidth).
  const blurViewBox = surround.blurPx > 0 ? (surround.blurPx * cw) / containerWidth : 0;
  const bboxCornerRadius = Math.min(bw, bh) * 0.03;

  const filterId = `sur-filter-${uid}`;
  const clipId = `bbox-clip-${uid}`;
  const vignetteId = `vignette-${uid}`;
  const hasFilter = surround.grayscale > 0 || surround.sepia > 0 || blurViewBox > 0;

  const colorMatrix: ReactNode = (() => {
    if (surround.sepia > 0) {
      const s = surround.sepia;
      const values = [
        0.393 * s + (1 - s), 0.769 * s, 0.189 * s, 0, 0,
        0.349 * s, 0.686 * s + (1 - s), 0.168 * s, 0, 0,
        0.272 * s, 0.534 * s, 0.131 * s + (1 - s), 0, 0,
        0, 0, 0, 1, 0,
      ].join(" ");
      return <feColorMatrix type="matrix" values={values} />;
    }
    if (surround.grayscale > 0) {
      return <feColorMatrix type="saturate" values={String(1 - surround.grayscale)} />;
    }
    return null;
  })();

  return (
    <div ref={containerRef} className={className} style={containerStyle}>
      <svg
        viewBox={`${cropX} ${cropY} ${cw} ${ch}`}
        className="block h-full w-full"
        preserveAspectRatio="none"
      >
        <defs>
          {hasFilter && (
            <filter id={filterId} x="0%" y="0%" width="100%" height="100%">
              {colorMatrix}
              {blurViewBox > 0 && <feGaussianBlur stdDeviation={blurViewBox} edgeMode="duplicate" />}
            </filter>
          )}
          <clipPath id={clipId}>
            <rect x={bx} y={by} width={bw} height={bh} rx={bboxCornerRadius} />
          </clipPath>
          {surround.vignette && (
            <radialGradient id={vignetteId} cx="50%" cy="50%" r="75%">
              <stop offset="40%" stopColor="black" stopOpacity={0} />
              <stop offset="100%" stopColor="black" stopOpacity={0.7} />
            </radialGradient>
          )}
        </defs>

        {/* Surround layer: full image with filter applied */}
        <image
          href={imageUrl}
          x={0}
          y={0}
          width={Wi}
          height={Hi}
          preserveAspectRatio="none"
          filter={hasFilter ? `url(#${filterId})` : undefined}
        />

        {/* Wash overlays covering the crop (below the unclipped bbox image) */}
        {effectiveDarken > 0 && (
          <rect x={cropX} y={cropY} width={cw} height={ch} fill="black" fillOpacity={effectiveDarken} />
        )}
        {surround.lighten > 0 && (
          <rect x={cropX} y={cropY} width={cw} height={ch} fill="white" fillOpacity={surround.lighten} />
        )}
        {surround.grayWash > 0 && (
          <rect x={cropX} y={cropY} width={cw} height={ch} fill="#6b7280" fillOpacity={surround.grayWash} />
        )}
        {surround.vignette && (
          <rect x={cropX} y={cropY} width={cw} height={ch} fill={`url(#${vignetteId})`} />
        )}

        {/* BBox region rendered sharp & uncovered on top */}
        <g clipPath={`url(#${clipId})`}>
          <image
            href={imageUrl}
            x={0}
            y={0}
            width={Wi}
            height={Hi}
            preserveAspectRatio="none"
          />
        </g>

        {overlayContent?.({ Wi, Hi, crop: { x: cropX, y: cropY, w: cw, h: ch } })}
      </svg>
    </div>
  );
}
